/**
 * QL-287 — Account Cash Overview body: one row per client account with its
 * bank cash, the net cash effect of the flow's portfolios, internal transfers,
 * the deposit needed and the cash left. Read-only — no add, edit, delete or
 * transfer controls. Every amount is a whole number in the account's currency.
 */

import type { AccountCashOverviewResponse } from "@repo/shared/types";
import { useState } from "react";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";
import type { AccountCashOverviewConfig } from "@/lib/types/flow";
import { T } from "./account-cash-overview-view.i18n";
import { AccountCashRow } from "./account-cash-row";
import type { FormatNumberFn } from "./types";

type AccountCashOverviewViewProps = {
  overview: AccountCashOverviewResponse;
  config: AccountCashOverviewConfig;
  language: string;
  fmt: FormatNumberFn;
  formatDateTime: (value: string) => string;
};

export function AccountCashOverviewView({
  overview,
  config,
  language,
  fmt,
  formatDateTime,
}: AccountCashOverviewViewProps) {
  const t = useT(T);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const footer = overview.balancesAsOf
    ? t.balancesAsOf(formatDateTime(overview.balancesAsOf))
    : t.balancesNeverSynced;

  return (
    <VisualizationContainer config={config}>
      {overview.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.noAccounts}</p>
      ) : (
        <Table variant="data">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-48">{t.account}</TableHead>
              <TableHead>{t.portfolios}</TableHead>
              <TableHead className="text-right">{t.startCash}</TableHead>
              <TableHead className="text-right">{t.netPortfolios}</TableHead>
              <TableHead className="text-right">{t.internalTransfers}</TableHead>
              <TableHead className="text-right">{t.depositNeeded}</TableHead>
              <TableHead className="text-right">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="cursor-help underline decoration-dotted underline-offset-4"
                    >
                      {t.cashLeft}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t.cashLeftHint}</TooltipContent>
                </Tooltip>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overview.rows.map((row) => (
              <AccountCashRow
                key={row.account.key}
                row={row}
                expanded={expanded.has(row.account.key)}
                onToggle={() => toggle(row.account.key)}
                language={language}
                fmt={fmt}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <p className="text-sm text-muted-foreground">{footer}</p>
    </VisualizationContainer>
  );
}
